import React from "react";

import usePageSeo from "../hooks/usePageSeo";
const styles = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=DM+Sans:wght@300;400;500;600&display=swap');

.about-page {
  font-family: 'DM Sans', sans-serif;
  background: #f9f7f4;
  color: #1a1a1a;
}

/* ─── Hero ─── */
.about-hero {
  background: #0f2e1d;
  padding: 96px 24px;
  text-align: center;
  position: relative;
  overflow: hidden;
}
.about-hero::before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse at 30% 50%, rgba(26,92,56,0.4) 0%, transparent 60%),
              radial-gradient(ellipse at 70% 50%, rgba(45,122,79,0.2) 0%, transparent 60%);
  pointer-events: none;
}
.about-hero-inner { max-width: 640px; margin: 0 auto; position: relative; z-index: 1; }
.about-hero-label {
  display: inline-block;
  background: rgba(255,255,255,0.1);
  color: #9ed4b4;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 6px 16px;
  border-radius: 100px;
  margin-bottom: 20px;
  border: 1px solid rgba(255,255,255,0.15);
}
.about-hero-title {
  font-family: 'Playfair Display', serif;
  font-size: clamp(2rem, 5vw, 3.2rem);
  font-weight: 700;
  color: #fff;
  line-height: 1.2;
  margin-bottom: 20px;
}
.about-hero-title em {
  font-style: italic;
  color: #9ed4b4;
}
.about-hero-subtitle {
  font-size: 1.05rem;
  color: #8aab97;
  line-height: 1.6;
}

/* ─── Section shared ─── */
.about-section {
  padding: 90px 24px;
}
.about-section-inner {
  max-width: 1160px;
  margin: 0 auto;
}
.about-section.bg-white { background: #fff; }
.about-section.bg-cream { background: #f9f7f4; }
.about-section.bg-dark  { background: #0f2e1d; }

.about-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #1a5c38;
  margin-bottom: 12px;
}
.about-label-light { color: #9ed4b4; }

.about-two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: center;
}
.about-two-col.reverse { direction: rtl; }
.about-two-col.reverse > * { direction: ltr; }

.about-h2 {
  font-family: 'Playfair Display', serif;
  font-size: clamp(1.6rem, 3vw, 2.4rem);
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1.2;
  margin-bottom: 20px;
}
.about-h2-light { color: #fff; }

.about-body {
  font-size: 1rem;
  color: #5a5a5a;
  line-height: 1.75;
  margin-bottom: 0;
}
.about-body-light { color: #8aab97; }

.about-img-wrap {
  border-radius: 24px;
  overflow: hidden;
  aspect-ratio: 4/3;
  background: linear-gradient(135deg, #e8f3ed 0%, #c8dfd0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 5rem;
  position: relative;
}
.about-img-wrap img {
  width: 100%; height: 100%;
  object-fit: cover;
}
.about-img-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: #1a5c38;
  opacity: 0.6;
}
.about-img-placeholder span:first-child { font-size: 4rem; }
.about-img-placeholder span:last-child {
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

/* ─── Mission values ─── */
.about-values-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-top: 56px;
}
.value-card {
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 20px;
  padding: 32px 24px;
  transition: background 0.2s, transform 0.2s;
}
.value-card:hover {
  background: rgba(255,255,255,0.1);
  transform: translateY(-3px);
}
.value-icon {
  font-size: 2rem;
  margin-bottom: 16px;
  display: block;
}
.value-title {
  font-family: 'DM Sans', sans-serif;
  font-size: 1rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 8px;
}
.value-desc {
  font-size: 0.88rem;
  color: #8aab97;
  line-height: 1.6;
}

/* ─── Stats row ─── */
.about-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2px;
  background: #e8e4df;
  border-radius: 20px;
  overflow: hidden;
  margin-top: 56px;
}
.stat-item {
  background: #fff;
  padding: 36px 24px;
  text-align: center;
}
.stat-value {
  font-family: 'Playfair Display', serif;
  font-size: 2.4rem;
  font-weight: 700;
  color: #1a5c38;
  line-height: 1;
  margin-bottom: 8px;
}
.stat-label {
  font-size: 0.88rem;
  color: #8a8a8a;
  font-weight: 500;
}

/* ─── CTA ─── */
.about-cta {
  background: #1a5c38;
  padding: 72px 24px;
  text-align: center;
}
.about-cta-inner { max-width: 560px; margin: 0 auto; }
.about-cta h2 {
  font-family: 'Playfair Display', serif;
  font-size: clamp(1.6rem, 3vw, 2.2rem);
  color: #fff;
  margin-bottom: 16px;
}
.about-cta p { color: rgba(255,255,255,0.75); margin-bottom: 32px; line-height: 1.6; }
.about-cta-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  color: #1a5c38;
  border: none;
  border-radius: 100px;
  padding: 14px 36px;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.2s, transform 0.15s;
}
.about-cta-btn:hover { background: #f0f0f0; transform: translateY(-1px); color: #1a5c38; }

/* ─── Responsive ─── */
@media (max-width: 900px) {
  .about-two-col, .about-two-col.reverse { grid-template-columns: 1fr; gap: 36px; direction: ltr; }
  .about-values-grid { grid-template-columns: repeat(2, 1fr); }
  .about-stats { grid-template-columns: 1fr; }
}
@media (max-width: 560px) {
  .about-values-grid { grid-template-columns: 1fr 1fr; gap: 14px; }
  .about-section { padding: 60px 20px; }
  .value-card { padding: 22px 16px; }
}
`;

const AboutUs = () => {
  usePageSeo({
    title: "About FMart Korea | Your Halal Grocery Store in Korea",
    description: "FMart Korea delivers certified halal groceries and halal meat across South Korea. Learn who we are, how we source our products and why families trust us.",
  });

  return (
    <>
      <style>{styles}</style>
      <div className="about-page">

        {/* ─── Hero ─── */}
        <section className="about-hero">
          <div className="about-hero-inner">
            <span className="about-hero-label">Our Story</span>
            <h1 className="about-hero-title">
              From Pakistan,<br />
              <em>with care</em>
            </h1>
            <p className="about-hero-subtitle">
              We bridge two homes — bringing the authentic flavors of Pakistan
              to Korean tables, with love, quality, and halal integrity.
            </p>
          </div>
        </section>

        {/* ─── Section 1: From Pakistan ─── */}
        <section className="about-section bg-white">
          <div className="about-section-inner">
            <div className="about-two-col">
              <div>
                <span className="about-label">Who We Are</span>
                <h2 className="about-h2">From Pakistan,<br />with Care</h2>
                <p className="about-body">
                  FMart was born from a simple longing — the taste of home. Founded by a
                  Pakistani family settled in Korea, we understand what it means to miss
                  the flavors you grew up with. Every product we stock is personally
                  selected, ethically sourced, and shipped fresh from Pakistan.
                </p>
                <p className="about-body" style={{ marginTop: "16px" }}>
                  From premium nuts and dry fruits to authentic spices and ready-to-eat
                  foods, everything at FMart carries the warmth of home-cooked
                  Pakistani heritage — now accessible across all of Korea.
                </p>
              </div>
              <div className="about-img-wrap">
                <div className="about-img-placeholder">
                  <span>🌿</span>
                  <span>Sourced from Pakistan</span>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="about-stats">
              <div className="stat-item">
                <div className="stat-value">12K+</div>
                <div className="stat-label">Happy Customers</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">100%</div>
                <div className="stat-label">Halal Verified</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">24h</div>
                <div className="stat-label">Seoul Delivery</div>
              </div>
            </div>
          </div>
        </section>

        {/* ─── Section 2: Bridge ─── */}
        <section className="about-section bg-cream">
          <div className="about-section-inner">
            <div className="about-two-col reverse">
              <div>
                <span className="about-label">Our Journey</span>
                <h2 className="about-h2">A Bridge Between<br />Two Homes</h2>
                <p className="about-body">
                  Korea is a beautiful country, but for many South Asian families, the
                  search for genuine halal groceries can be exhausting. FMart was created
                  to fill that gap — not just as a store, but as a community resource.
                </p>
                <p className="about-body" style={{ marginTop: "16px" }}>
                  We work directly with farmers and suppliers in Pakistan, cutting out
                  middlemen to ensure freshness, traceability, and the best prices.
                  Every bag of almonds, every packet of spices, every bottle of oil tells
                  a story of direct trade and genuine care.
                </p>
              </div>
              <div className="about-img-wrap">
                <div className="about-img-placeholder">
                  <span>🤝</span>
                  <span>Direct Trade</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ─── Section 3: Mission ─── */}
        <section className="about-section bg-dark">
          <div className="about-section-inner">
            <div style={{ textAlign: "center", maxWidth: "560px", margin: "0 auto" }}>
              <span className="about-label about-label-light">Our Mission</span>
              <h2 className="about-h2 about-h2-light">
                Quality, Halal,<br />Authenticity
              </h2>
              <p className="about-body about-body-light">
                Our mission is simple: make authentic, halal Pakistani food accessible
                to every household in Korea — without compromise.
              </p>
            </div>

            <div className="about-values-grid">
              <div className="value-card">
                <span className="value-icon">☪️</span>
                <div className="value-title">Halal First</div>
                <div className="value-desc">
                  Every single product is halal certified. No exceptions, no shortcuts.
                </div>
              </div>
              <div className="value-card">
                <span className="value-icon">🌱</span>
                <div className="value-title">Naturally Sourced</div>
                <div className="value-desc">
                  We work directly with farms to bring natural, unprocessed goodness.
                </div>
              </div>
              <div className="value-card">
                <span className="value-icon">🇰🇷</span>
                <div className="value-title">Made for Korea</div>
                <div className="value-desc">
                  Our packaging, delivery, and service are built for the Korean market.
                </div>
              </div>
              <div className="value-card">
                <span className="value-icon">🤝</span>
                <div className="value-title">Direct Trade</div>
                <div className="value-desc">
                  We trade directly with Pakistani suppliers for fair prices and freshness.
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ─── CTA ─── */}
        <section className="about-cta">
          <div className="about-cta-inner">
            <h2>Ready to taste the difference?</h2>
            <p>
              Explore our full range of premium halal products — nuts, dry fruits,
              spices, and more — delivered fresh to your door across Korea.
            </p>
            <a href="/shop" className="about-cta-btn">
              Shop Now →
            </a>
          </div>
        </section>

      </div>
    </>
  );
};

export default AboutUs;